import { useState, useEffect } from 'react';
import { IconSearch } from '../components/Icons';
import PublicationModal from '../components/PublicationModal';
import useScrollReveal from '../hooks/useScrollReveal';
import './Research.css';

export default function Research() {
  useScrollReveal();
  const [pubs,           setPubs]           = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery,    setSearchQuery]    = useState('');
  const [selectedPub,    setSelectedPub]    = useState(null);

  useEffect(() => {
    fetch('/api/publications')
      .then(r => r.json())
      .then(data => { 
        // Handle both new metadata format and old flat array fallback
        if (data && data.items) {
          setPubs(data.items);
          setCategories(data.categories || []);
        } else if (Array.isArray(data)) {
          setPubs(data);
          setCategories([]);
        } else {
          setPubs([]);
          console.error("Publications API error:", data);
        }
        setLoading(false); 
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setLoading(false);
      });
  }, []);

  // Standard category labels/badges if not provided by backend
  const getCategoryLabel = (id) => categories.find(c => c.id === id)?.label || id.charAt(0).toUpperCase() + id.slice(1);
  const getCategoryBadge = (id) => categories.find(c => c.id === id)?.badge || 'badge-navy';

  // Build filter list (Always include 'All')
  const filterTabs = [{ id: 'all', label: 'All' }, ...categories];

  const filtered = (pubs || [])
    .filter(p => {
      if (!p) return false;
      const matchCat = activeCategory === 'all' || p.category === activeCategory;
      const q = (searchQuery || '').toLowerCase();
      const matchQ = !q
        || (String(p.title || '')).toLowerCase().includes(q)
        || (String(p.journal || '')).toLowerCase().includes(q)
        || (String(p.publisher || '')).toLowerCase().includes(q)
        || (String(p.outlet || '')).toLowerCase().includes(q)
        || (p.tags || []).some(t => (String(t || '')).toLowerCase().includes(q));
      return matchCat && matchQ;
    })
    .sort((a, b) => {
      if (!a || !b) return 0;
      // 1. Prioritize those with covers
      if (a.coverUrl && !b.coverUrl) return -1;
      if (!a.coverUrl && b.coverUrl) return 1;
      
      // 2. Then sort by year (descending)
      const yearA = parseInt(a.year || 0) || 0;
      const yearB = parseInt(b.year || 0) || 0;
      return yearB - yearA;
    });

  return (
    <main>
      <section className="page-hero" aria-labelledby="research-heading">
        <div className="container reveal reveal-up">
          <span className="section-label">Scholarship</span>
          <h1 id="research-heading">Research &amp; Publications</h1>
          <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '1.5rem' }}>
            Peer-reviewed journal articles, books, monographs, and research reports spanning two decades of scholarship on religion, ethics, and sustainability.
          </p>
          <div className="research__profiles">
            <a href="https://www.scopus.com/authid/detail.uri?authorId=6503908870" target="_blank" rel="noopener noreferrer" className="research__profile-link">
              <strong>Scopus</strong> <span>6503908870</span>
            </a>
            <a href="https://orcid.org/0000-0002-3162-206X" target="_blank" rel="noopener noreferrer" className="research__profile-link">
              <strong>ORCID</strong> <span>0000-0002-3162-206X</span>
            </a>
            <a href="https://www.webofscience.com/wos/author/record/GPP-4658-2022" target="_blank" rel="noopener noreferrer" className="research__profile-link">
              <strong>WOS ID</strong> <span>GPP-4658-2022</span>
            </a>
            <a href="https://sinta.kemdikbud.go.id/authors/profile/6007235" target="_blank" rel="noopener noreferrer" className="research__profile-link">
              <strong>SINTA ID</strong> <span>6007235</span>
            </a>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="research__controls">
            <div className="research__search">
              <span className="research__search-icon" style={{ display: 'flex' }}><IconSearch size={18} /></span>
              <input
                id="publications-search"
                type="text"
                placeholder="Search publications, journals, topics…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="research__search-input"
              />
            </div>
            <div className="research__filters">
              {filterTabs.map(cat => (
                <button
                  key={cat.id}
                  id={`filter-${cat.id}`}
                  className={`research__filter-btn ${activeCategory === cat.id ? 'research__filter-btn--active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.label}
                  <span className="research__filter-count">
                    {pubs.filter(p => cat.id === 'all' || p.category === cat.id).length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="research__loading">Loading publications…</div>
          ) : (
            <>
              <p className="research__result-count">
                Showing <strong>{filtered.length}</strong> publication{filtered.length !== 1 ? 's' : ''}
              </p>
              <div className="research__list">
                {filtered.length === 0 ? (
                  <div className="research__empty">
                    <p>No publications found.</p>
                    <button className="btn btn-outline btn-sm" onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}>Clear filters</button>
                  </div>
                ) : filtered.map((pub, i) => (
                  <article key={pub.id} className={`pub-card ${pub.coverUrl ? 'pub-card--with-cover' : ''} reveal reveal-up delay-${(i % 3) + 1}`} id={`pub-${pub.id}`}>
                    {pub.coverUrl && (
                      <div className="pub-card__left">
                        <div className="pub-card__meta-left">
                          <span className={`badge ${getCategoryBadge(pub.category)}`}>
                            {getCategoryLabel(pub.category)}
                          </span>
                        </div>
                        <div className="pub-card__cover" onClick={() => setSelectedPub(pub)} title="Click to view details">
                          <img src={pub.coverUrl} alt={`Cover for ${pub.title || 'Publication'}`} loading="lazy" />
                          <div className="pub-card__cover-overlay">
                            <span>🔍 View Details</span>
                          </div>
                        </div>
                        <button className="btn btn-navy btn-sm pub-card__detail-btn-desktop" onClick={() => setSelectedPub(pub)}>
                          View Details
                        </button>
                      </div>
                    )}
                    <span className="pub-card__year">{pub.year || pub.date}</span>
                    <div className="pub-card__content">
                      <div className="pub-card__header">
                        {!pub.coverUrl ? (
                          <span className={`badge ${getCategoryBadge(pub.category)}`}>
                            {getCategoryLabel(pub.category)}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="pub-card__title" onClick={() => setSelectedPub(pub)} style={{ cursor: 'pointer' }}>{pub.title}</h3>
                      <p className="pub-card__source">
                        {pub.journal || pub.publisher || pub.outlet || pub.bookTitle}
                        {pub.volume && `, Vol. ${pub.volume}`}
                        {pub.issue  && `, No. ${pub.issue}`}
                        {pub.pages  && `, pp. ${pub.pages}`}
                      </p>
                      {(pub.abstract || pub.description) && (
                        <p className="pub-card__abstract">
                          {String(pub.abstract || pub.description).length > 240 
                            ? String(pub.abstract || pub.description).substring(0, 240) + '...'
                            : String(pub.abstract || pub.description)
                          }
                        </p>
                      )}
                      {pub.tags?.length > 0 && (
                        <div className="pub-card__tags">
                          {pub.tags.map(t => <span key={t} className="badge badge-navy">{t}</span>)}
                        </div>
                      )}
                      <div className="pub-card__actions" style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-navy btn-sm pub-card__detail-btn-mobile" onClick={() => setSelectedPub(pub)}>
                          View Details
                        </button>
                        {pub.doi && (
                          <a 
                            href={String(pub.doi).startsWith('http') ? pub.doi : `https://doi.org/${String(pub.doi).trim().replace(/^doi:/i, '')}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-outline btn-sm pub-card__link"
                          >
                            DOI
                          </a>
                        )}
                        {pub.url && (
                          <a href={pub.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm pub-card__link">
                            Google Scholar
                          </a>
                        )}
                        {pub.fileUrl && (
                          <a href={pub.fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm pub-card__link" download>
                            PDF
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}

          {selectedPub && (
            <PublicationModal pub={selectedPub} onClose={() => setSelectedPub(null)} />
          )}
        </div>
      </section>
    </main>
  );
}
